'use client'

// @ts-strict-ignore
import clsx from 'clsx'
import { ChangeEvent, FocusEvent, forwardRef, KeyboardEvent, MouseEvent, useState } from 'react'

import { Input, InputProps } from '../../Input'

import cl from './Autocomplete.module.scss'

export type AutocompleteOption = {
	label: string
	value: string
	helpText?: string
	[key: string]: string
}

interface AutocompleteProps extends Omit<InputProps, 'className'> {
	className?: string
	options?: AutocompleteOption[]
	open?: boolean
	onSelectOption?: (value: string) => void
}

export const Autocomplete = forwardRef<HTMLInputElement, AutocompleteProps>((props, ref) => {
	const { className, value, options = [], open, onSelectOption, onChange, onFocus, onBlur, ...rest } = props

	const [innerValue, setInnerValue] = useState('')
	const [innerOpen, setInnerOpen] = useState(false)
	const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1)

	const activeValue = value ? value : innerValue

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		setInnerOpen(true)
		setSelectedOptionIndex(-1)

		if (onChange) {
			return onChange(e)
		} else {
			setInnerValue(e.target.value)
		}
	}

	const handleSelectOption = (value: string, e?: MouseEvent | KeyboardEvent) => {
		e.stopPropagation()
		e.preventDefault()

		setSelectedOptionIndex(-1)

		if (onSelectOption) {
			return onSelectOption(value)
		} else {
			setInnerValue(value)
		}
	}

	const handleFocus = (e: FocusEvent<HTMLInputElement, Element>) => {
		setInnerOpen(true)
		onFocus?.(e)
	}

	const handleBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
		e.preventDefault()
		e.stopPropagation()

		setSelectedOptionIndex(-1)
		onBlur?.(e)

		setTimeout(() => {
			setInnerOpen(false)
		}, 200)
	}

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		switch (e.key) {
			case 'ArrowUp':
				e.preventDefault()
				setSelectedOptionIndex(prevIndex => Math.max(prevIndex - 1, 0))
				break
			case 'ArrowDown':
				e.preventDefault()
				setSelectedOptionIndex(prevIndex => Math.min(prevIndex + 1, options.length - 1))
				break
			case 'Enter':
				if (selectedOptionIndex !== -1) {
					handleSelectOption(options[selectedOptionIndex].value, e)
				}
				break
			default:
				break
		}
	}

	const optionsClasses = clsx(cl.options, className, {
		[cl.options__open]: open === undefined ? innerOpen : open
	})

	const liClasses = (isSelected: boolean) =>
		clsx(cl.option, {
			[cl.option__selected]: isSelected
		})

	return (
		<div className={clsx(cl.root, className)} onBlur={handleBlur}>
			<Input
				ref={ref}
				aria-autocomplete="list"
				aria-controls="autocomplete-options"
				value={activeValue}
				onChange={handleInputChange}
				onKeyDown={handleKeyDown}
				onFocus={handleFocus}
				// onBlur={handleBlur}
				{...rest}
			/>
			<div className={optionsClasses}>
				<ul className={cl.options__list} role="listbox" aria-label="Список с автоподсказками">
					{options.map((option, i) => (
						<li
							key={option.label}
							className={liClasses(selectedOptionIndex === i)}
							role="option"
							aria-selected={activeValue === option.value}
							onClick={e => handleSelectOption(option.value, e)}
							onMouseEnter={() => setSelectedOptionIndex(i)}>
							<p className={cl.option__title}>{option.label}</p>
							{option.helpText && <p className={cl.option__helpText}>{option.helpText}</p>}
						</li>
					))}
				</ul>
			</div>
		</div>
	)
})
